import { useEffect, useState } from "react";
import { useSeriesStore } from "../../../stores/seriesStore";
import SVG from "react-inlinesvg";
import classNames from "classnames";
import { closestCenter, DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Container, Header, BookList } from "./styles";
import { AnimatePresence, motion } from "framer-motion";
import { useAllFoldersStore } from "../store";

const SortableBookItem = ({ item }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.projectId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      className="book-list-item"
      style={style}
      key={item.projectId}
      ref={setNodeRef}
    >
      <div
        className="grabber"
        {...attributes}
        {...listeners}
      >
        <SVG
          src="/assets/grabber.svg"
          height={24}
          width={24}
        />
      </div>
      <p className="book-title">{item.title}</p>
    </li>
  );
};

const SeriesContinuity = ({ folder, projects }) => {
  const collapsed = useSeriesStore((state) => state.collapsed);
  const setCollapsed = useSeriesStore((state) => state.setCollapsed);
  const getContentsBySeriesId = useSeriesStore((state) => state.getContentsBySeriesId);
  const contents = useSeriesStore((state) => state.contents);
  const updateSeriesOrder = useAllFoldersStore((state) => state.updateSeriesOrder);
  const [localContents, setLocalContents] = useState([]);

  // Initialize contents from projects
  useEffect(() => {
    const initialContents = projects
      .sort((a, b) => a.series_order - b.series_order)
      .map((project) => ({
        id: project.id,
        projectId: project.firebase_id,
        title: project.title,
        seriesOrder: project.series_order,
      }));

    setLocalContents(initialContents);
  }, [contents, contents.length, projects, projects.length]);

  // Sync with series contents when they load
  useEffect(() => {
    if (contents?.length > 0) {
      setLocalContents(contents);
    }
  }, [contents, contents.length]);

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over.id) {
      const oldIndex = localContents.findIndex((item) => item.projectId === active.id);
      const newIndex = localContents.findIndex((item) => item.projectId === over.id);
      const item = localContents[oldIndex];

      const updatedContents = [...localContents];
      updatedContents.splice(oldIndex, 1);
      updatedContents.splice(newIndex, 0, item);

      // Update local state immediately
      setLocalContents(updatedContents);

      // Calculate new series order
      const updates = updatedContents.map((proj, idx) => ({
        id: proj.id,
        projectId: proj.projectId,
        seriesOrder: idx + 1,
      }));

      // Updates server and parent component projects
      updateSeriesOrder(updates, folder);
    }
  };

  const motionProps = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: "auto" },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.25, ease: "easeInOut" },
  };

  useEffect(() => {
    if (folder && folder.series_id) {
      getContentsBySeriesId(folder.series_id);
    }
  }, [folder, getContentsBySeriesId]);

  if (!folder.series_id) return null;

  return (
    <Container className={classNames("series-continuity-container", { collapsed })}>
      <Header>
        <div className="sc-title-container">
          <div className="sc-title">
            <SVG
              src="/assets/series.svg"
              height={32}
              width={32}
            />
            <h3>Series Timeline</h3>
          </div>
          <SVG
            src="/assets/chevron-down.svg"
            className={classNames("chevron", { "rotated-chevron": !collapsed, hide: localContents.length === 0 })}
            alt="disclosure arrow"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={`${collapsed ? "Expand" : "Collapse"} series continuity menu`}
          />
        </div>
        <div className="sc-subtitle-container">
          <p>Drag your projects into the correct order for an accurate timeline.</p>
        </div>
      </Header>
      <AnimatePresence>
        {!collapsed && (
          <motion.div {...motionProps}>
            <DndContext
              onDragEnd={handleDragEnd}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
            >
              <BookList>
                <SortableContext
                  items={localContents.map((item) => item.projectId)}
                  strategy={verticalListSortingStrategy}
                >
                  {localContents.map((item) => (
                    <SortableBookItem
                      key={item.id}
                      item={item}
                    />
                  ))}
                </SortableContext>
              </BookList>
            </DndContext>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default SeriesContinuity;
